
export class Grid2D<T> {
  readonly size: { x: number; y: number };
  readonly length: number;
  readonly items: T[];

  constructor(size: { x: number, y: number });
  constructor(x: number, y: number);
  constructor(arg1: any, arg2?: number) {
    if (typeof arg1 === 'object') {
      this.size = { x: arg1.x, y: arg1.y };
    }
    else {
      this.size = { x: arg1, y: arg2 };
    }

    this.length = this.size.y * this.size.x;

    this.items = [];
    this.items.length = this.length;
  }

  getIndex(x: number, y: number): number {
    return y * this.size.x + x;
  }

  getAt(index: number): T | undefined;
  getAt(position: { x: number; y: number }): T | undefined;
  getAt(x: number, y: number): T | undefined;
  getAt(arg1: any, arg2?: any): T | undefined {
    let index: number;

    if (typeof arg1 === 'number') {
      if (typeof arg2 === 'number') {
        index = this.getIndex(arg1, arg2);
      }
      else {
        index = arg1;
      }
    }
    else {
      index = this.getIndex(arg1.x, arg1.y);
    }

    if (index < 0 || index >= this.length)
      throw new Error(`Invalid grid location: index: ${index}`);

    return this.items[index];
  }

  setAt(index: number, value: T): void;
  setAt(position: { x: number; y: number }, value: T): void;
  setAt(x: number, y: number, value: T): void;
  setAt(arg1: any, arg2?: any, arg3?: any): void {
    let index: number;
    let value: T;

    if (arguments.length === 3) {
      index = this.getIndex(arg1, arg2);
      value = arg3;
    }
    else {
      if (typeof arg1 === 'number') {
        index = arg1;
        value = arg2;
      }
      else {
        index = this.getIndex(arg1.x, arg1.y);
        value = arg2;
      }
    }

    if (index < 0 || index >= this.length)
      throw new Error(`Invalid grid location: index: ${index}`);

    this.items[index] = value;
  }

  generate(generator: (x: number, y: number) => T | undefined): void;
  generate(generator: (x: number, y: number, index: number) => T | undefined): void {
    for (let y = 0; y < this.size.y; ++y) {
      for (let x = 0; x < this.size.x; ++x) {
        let index = this.getIndex(x, y);

        this.items[index] = generator(x, y, index);
      }
    }
  }

  /**
   * Looks for a space, extending from startX and startY, where the given rectangle can be placed
   * Returns null, if starting from the position, the extension can not be made
   * This function assumes the starting location is valid, and will not check it
   * */
  findSpaceToExtend(startX: number, startY: number, width: number, height: number, ignoreItem?: (item: T) => boolean) {
    if (ignoreItem === undefined) {
      ignoreItem = () => false;
    }

    let maxX = Math.min(startX + width, this.size.x);
    let maxY = Math.min(startY + height, this.size.y);

    // Go left and right, count empty columns
    // Note, a column is only considered empty, if it has enough row space as well (see checkHeight)
    let freeWidth = 1;

    let minX = startX;
    let minY = startY;

    // scans up and down, and counts how much space there is in a column,
    // then returns whether the visual can fit in, by height
    let checkHeight = (x: number) => {
      let freeHeight = 1;

      for (let y = startY - 1; y >= 0; --y) {
        let item = this.getAt(x, y);

        if (item === undefined || !ignoreItem(item)) {
          freeHeight++;
          minY = Math.min(y, minY);
        }
      }

      for (let y = startY + 1; y < maxY; ++y) {
        let item = this.getAt(x, y);

        if (item === undefined || !ignoreItem(item)) {
          freeHeight++;
        }
      }

      return freeHeight >= height;
    }

    // scan left and right, and count how much space there is in a row
    // a cell is considered only if the column can fit the visual (see checkHeight)
    for (let x = startX - 1; x >= 0; --x) {
      let item = this.getAt(x, startY);

      if (item === undefined || !ignoreItem(item)) {
        if (checkHeight(x)) {
          freeWidth++;
          minX = Math.min(x, minX);
        }
      }
    }

    for (let x = startX + 1; x < maxX; ++x) {
      let item = this.getAt(x, startY);

      if (item === undefined || !ignoreItem(item)) {
        if (checkHeight(x)) {
          freeWidth++;
        }
      }
    }

    if (freeWidth < width)
      return null;

    return { x: minX, y: minY };
  }

  /**
   * Finds location in the grid, where a rectangle can be placed
   * */
  findSpaceToPlace(width: number, height: number, ignoreItem?: (item: T) => boolean) {
    if (ignoreItem === undefined) {
      ignoreItem = () => false;
    }

    for (let y = 0; y < this.size.y; ++y) {
      if ((y + height) > this.size.y)
        continue;

      for (let x = 0; x < this.size.x; ++x) {
        if ((x + width) > this.size.x)
          continue;

        let enoughSpace = true;
        let breakLoop = false;

        for (let dx = 0; dx < width; ++dx) {
          for (let dy = 0; dy < height; ++dy) {
            let item = this.getAt(x + dx, y + dy);

            if (!ignoreItem(item)) {
              // the current cell is occupied, so the rect does not fit; break out
              enoughSpace = false;
              breakLoop = true;
              break;
            }
          }

          if (breakLoop)
            break;
        }

        if (enoughSpace) {
          return { x, y };
        }
      }
    }

    return null;
  }
};
